import React, { useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  Checkbox,
  Chip,
  CircularProgress,
  FormControl,
  FormHelperText,
  FormLabel,
  Grid,
  IconButton,
  Input,
  Modal,
  ModalClose,
  ModalDialog,
  Option,
  Select,
  Stack,
  Typography,
} from '@mui/joy';
import {
  ArrowCounterClockwise,
  PencilSimple,
  Plus,
  Trash,
} from '@phosphor-icons/react';
import { ResponsiveTable } from '../../components/common/ResponsiveTable';
import { useAuth } from '../../contexts/AuthContext';
import {
  useTakeoffItems,
  useCreateTakeoffItem,
  useUpdateTakeoffItem,
  useDeleteTakeoffItem,
  useResetTakeoffItems,
  type TakeoffLineItem,
} from '../../hooks/measurements/useTakeoffItems';
import {
  CATEGORY_LABELS,
  CATEGORY_ORDER,
  DRIVER_LABELS,
  MATERIAL_TYPES,
} from '../../components/measurements/takeoffConstants';

interface ItemFormState {
  name: string;
  category: TakeoffLineItem['category'];
  unit_label: string;
  driver: TakeoffLineItem['driver'];
  multiplier: string;
  coverage_per_unit: string;
  include_waste: boolean;
  min_quantity: string;
  applies_to: string[];
  sort_order: string;
  is_active: boolean;
}

const EMPTY_FORM: ItemFormState = {
  name: '',
  category: 'field_materials',
  unit_label: 'pcs',
  driver: 'squares',
  multiplier: '1',
  coverage_per_unit: '1',
  include_waste: false,
  min_quantity: '0',
  applies_to: [],
  sort_order: '0',
  is_active: true,
};

function formToPayload(form: ItemFormState) {
  return {
    name: form.name.trim(),
    category: form.category,
    unit_label: form.unit_label.trim() || 'pcs',
    driver: form.driver,
    multiplier: form.multiplier || '1',
    coverage_per_unit: form.coverage_per_unit || '1',
    include_waste: form.include_waste,
    min_quantity: Math.max(0, Math.round(Number(form.min_quantity) || 0)),
    applies_to: form.applies_to,
    sort_order: Math.max(0, Math.round(Number(form.sort_order) || 0)),
    is_active: form.is_active,
  };
}

function itemToForm(item: TakeoffLineItem): ItemFormState {
  return {
    name: item.name,
    category: item.category,
    unit_label: item.unit_label,
    driver: item.driver,
    multiplier: String(Number(item.multiplier)),
    coverage_per_unit: String(Number(item.coverage_per_unit)),
    include_waste: item.include_waste,
    min_quantity: String(item.min_quantity),
    applies_to: item.applies_to,
    sort_order: String(item.sort_order),
    is_active: item.is_active,
  };
}

function formulaSummary(item: TakeoffLineItem): string {
  const multiplier = Number(item.multiplier);
  const coverage = Number(item.coverage_per_unit);
  if (item.driver === 'fixed') {
    return `Fixed ${Math.max(item.min_quantity, Math.ceil(multiplier / coverage))} ${item.unit_label}`;
  }
  let formula = DRIVER_LABELS[item.driver];
  if (multiplier !== 1) formula += ` × ${multiplier}`;
  if (coverage !== 1) formula += ` ÷ ${coverage}`;
  return formula;
}

export const MaterialTakeoffSettingsPage: React.FC = () => {
  const { user } = useAuth();
  const { data: items, isLoading, error } = useTakeoffItems();
  const createMutation = useCreateTakeoffItem();
  const updateMutation = useUpdateTakeoffItem();
  const deleteMutation = useDeleteTakeoffItem();
  const resetMutation = useResetTakeoffItems();

  const [editing, setEditing] = useState<{ item: TakeoffLineItem | null; form: ItemFormState } | null>(null);
  const [formError, setFormError] = useState<string | null>(null);

  if (!user?.is_organization_admin) {
    return (
      <Alert color="warning">
        Only organization admins can manage Material Takeoff defaults.
      </Alert>
    );
  }

  const handleSave = async () => {
    if (!editing) return;
    const payload = formToPayload(editing.form);
    if (!payload.name) {
      setFormError('Name is required.');
      return;
    }
    if (!(Number(payload.multiplier) > 0) || !(Number(payload.coverage_per_unit) > 0)) {
      setFormError('Multiplier and coverage must be greater than 0.');
      return;
    }
    setFormError(null);
    try {
      if (editing.item) {
        await updateMutation.mutateAsync({ id: editing.item.id, data: payload });
      } else {
        await createMutation.mutateAsync(payload);
      }
      setEditing(null);
    } catch (err) {
      const detail = (err as { response?: { data?: unknown } })?.response?.data;
      setFormError(detail ? JSON.stringify(detail) : 'Failed to save item. Please try again.');
    }
  };

  const handleDelete = async (item: TakeoffLineItem) => {
    if (!window.confirm(`Delete "${item.name}" from your takeoff catalog?`)) return;
    try {
      await deleteMutation.mutateAsync(item.id);
    } catch {
      window.alert('Failed to delete the item. Please try again.');
    }
  };

  const handleReset = async () => {
    if (!window.confirm(
      'Restore the default catalog? This deletes all custom items and edits for your organization and cannot be undone.'
    )) return;
    try {
      await resetMutation.mutateAsync();
    } catch {
      window.alert('Failed to restore defaults. Please try again.');
    }
  };

  const appliesToLabel = (item: TakeoffLineItem): string => {
    if (!item.applies_to.length) return 'All materials';
    return item.applies_to
      .map((key) => MATERIAL_TYPES.find((m) => m.value === key)?.label || key)
      .join(', ');
  };

  return (
    <Stack spacing={3}>
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        spacing={1.5}
        sx={{ justifyContent: 'space-between', alignItems: { xs: 'stretch', sm: 'flex-start' } }}
      >
        <Box>
          <Typography level="h3">Material Takeoff</Typography>
          <Typography level="body-sm" sx={{ color: 'text.tertiary', mt: 0.5 }}>
            Customize how quantities are calculated on the Material Takeoff tab.
            These settings apply to everyone in your organization and never change the measurements themselves.
          </Typography>
        </Box>
        <Stack direction="row" spacing={1}>
          <Button
            variant="outlined"
            color="neutral"
            size="sm"
            startDecorator={<ArrowCounterClockwise size={16} />}
            loading={resetMutation.isPending}
            onClick={handleReset}
          >
            Restore Defaults
          </Button>
          <Button
            variant="solid"
            color="primary"
            size="sm"
            startDecorator={<Plus size={16} />}
            onClick={() => setEditing({ item: null, form: { ...EMPTY_FORM } })}
          >
            Add Item
          </Button>
        </Stack>
      </Stack>

      {isLoading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress />
        </Box>
      )}
      {!!error && !isLoading && (
        <Alert color="danger">Failed to load your takeoff catalog. Please refresh the page.</Alert>
      )}

      {!isLoading && !error && CATEGORY_ORDER.map((category) => {
        const rows = (items || [])
          .filter((item) => item.category === category)
          .sort((a, b) => a.sort_order - b.sort_order || a.id - b.id);
        if (!rows.length) return null;
        return (
          <Card key={category} variant="outlined">
            <Typography
              level="title-sm"
              sx={{
                textTransform: 'uppercase',
                fontSize: '11px',
                fontWeight: 'bold',
                letterSpacing: '0.5px',
                color: 'warning.500',
                mb: 1,
              }}
            >
              {CATEGORY_LABELS[category]}
            </Typography>
            <ResponsiveTable<TakeoffLineItem>
              rows={rows}
              getRowKey={(item) => item.id}
              columns={[
                {
                  key: 'name',
                  sortValue: (row) => row.name,
                  header: 'Material',
                  mobileVariant: 'header',
                  render: (item) => (
                    <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
                      <Typography
                        level="title-sm"
                        sx={{ fontWeight: 600, ...(item.is_active ? {} : { color: 'text.tertiary', textDecoration: 'line-through' }) }}
                      >
                        {item.name}
                      </Typography>
                      {!item.is_active && (
                        <Chip size="sm" variant="soft" color="neutral">Hidden</Chip>
                      )}
                    </Stack>
                  ),
                },
                {
                  key: 'formula',
                  header: 'Quantity Formula',
                  mobileVariant: 'body',
                  render: (item) => (
                    <Typography level="body-xs" sx={{ color: 'text.secondary' }}>
                      {formulaSummary(item)}
                      {item.include_waste ? ' • includes waste %' : ''}
                      {item.min_quantity > 0 ? ` • min ${item.min_quantity}` : ''}
                    </Typography>
                  ),
                },
                {
                  key: 'unit',
                  sortValue: (row) => row.unit_label,
                  header: 'Unit',
                  render: (item) => (
                    <Typography level="body-xs" sx={{ color: 'text.tertiary' }}>{item.unit_label}</Typography>
                  ),
                },
                {
                  key: 'applies',
                  header: 'Applies To',
                  render: (item) => (
                    <Typography level="body-xs" sx={{ color: 'text.tertiary' }}>
                      {appliesToLabel(item)}
                    </Typography>
                  ),
                },
              ]}
              rowActions={(item) => (
                <Stack direction="row" spacing={0.5}>
                  <IconButton
                    size="sm"
                    variant="plain"
                    aria-label={`Edit ${item.name}`}
                    onClick={() => setEditing({ item, form: itemToForm(item) })}
                  >
                    <PencilSimple size={16} />
                  </IconButton>
                  <IconButton
                    size="sm"
                    variant="plain"
                    color="danger"
                    aria-label={`Delete ${item.name}`}
                    onClick={() => handleDelete(item)}
                  >
                    <Trash size={16} />
                  </IconButton>
                </Stack>
              )}
            />
          </Card>
        );
      })}

      {/* Add / Edit modal */}
      <Modal open={!!editing} onClose={() => setEditing(null)}>
        <ModalDialog sx={{ width: { xs: '95vw', sm: 560 }, maxWidth: '95vw', overflowY: 'auto' }}>
          <ModalClose />
          <Typography level="title-lg" sx={{ mb: 1 }}>
            {editing?.item ? `Edit ${editing.item.name}` : 'Add Takeoff Item'}
          </Typography>
          {editing && (
            <Stack spacing={2}>
              <FormControl required>
                <FormLabel>Name</FormLabel>
                <Input
                  value={editing.form.name}
                  onChange={(e) => setEditing({ ...editing, form: { ...editing.form, name: e.target.value } })}
                  placeholder="e.g. Synthetic Underlayment"
                />
              </FormControl>

              <Grid container spacing={2}>
                <Grid xs={12} sm={6}>
                  <FormControl>
                    <FormLabel>Category</FormLabel>
                    <Select
                      value={editing.form.category}
                      onChange={(_, v) => v && setEditing({ ...editing, form: { ...editing.form, category: v } })}
                    >
                      {CATEGORY_ORDER.map((c) => (
                        <Option key={c} value={c}>{CATEGORY_LABELS[c]}</Option>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid xs={12} sm={6}>
                  <FormControl>
                    <FormLabel>Unit Label</FormLabel>
                    <Input
                      value={editing.form.unit_label}
                      onChange={(e) => setEditing({ ...editing, form: { ...editing.form, unit_label: e.target.value } })}
                      placeholder="e.g. rolls, pcs (10ft), boxes"
                    />
                  </FormControl>
                </Grid>
              </Grid>

              <FormControl>
                <FormLabel>Calculated From</FormLabel>
                <Select
                  value={editing.form.driver}
                  onChange={(_, v) => v && setEditing({ ...editing, form: { ...editing.form, driver: v } })}
                >
                  {(Object.keys(DRIVER_LABELS) as TakeoffLineItem['driver'][]).map((d) => (
                    <Option key={d} value={d}>{DRIVER_LABELS[d]}</Option>
                  ))}
                </Select>
                <FormHelperText>
                  Quantity = driver × multiplier ÷ coverage, rounded up.
                </FormHelperText>
              </FormControl>

              <Grid container spacing={2}>
                <Grid xs={6} sm={4}>
                  <FormControl>
                    <FormLabel>Multiplier</FormLabel>
                    <Input
                      type="number"
                      slotProps={{ input: { min: 0, step: 'any' } }}
                      value={editing.form.multiplier}
                      onChange={(e) => setEditing({ ...editing, form: { ...editing.form, multiplier: e.target.value } })}
                    />
                  </FormControl>
                </Grid>
                <Grid xs={6} sm={4}>
                  <FormControl>
                    <FormLabel>Coverage / Unit</FormLabel>
                    <Input
                      type="number"
                      slotProps={{ input: { min: 0, step: 'any' } }}
                      value={editing.form.coverage_per_unit}
                      onChange={(e) => setEditing({ ...editing, form: { ...editing.form, coverage_per_unit: e.target.value } })}
                    />
                  </FormControl>
                </Grid>
                <Grid xs={6} sm={4}>
                  <FormControl>
                    <FormLabel>Minimum Qty</FormLabel>
                    <Input
                      type="number"
                      slotProps={{ input: { min: 0, step: 1 } }}
                      value={editing.form.min_quantity}
                      onChange={(e) => setEditing({ ...editing, form: { ...editing.form, min_quantity: e.target.value } })}
                    />
                  </FormControl>
                </Grid>
              </Grid>

              <FormControl>
                <FormLabel>Applies To</FormLabel>
                <Select
                  multiple
                  value={editing.form.applies_to}
                  onChange={(_, v) => setEditing({ ...editing, form: { ...editing.form, applies_to: v } })}
                  placeholder="All roofing materials"
                  renderValue={(selected) =>
                    selected.length
                      ? selected.map((s) => MATERIAL_TYPES.find((m) => m.value === s.value)?.label || s.value).join(', ')
                      : 'All roofing materials'
                  }
                >
                  {MATERIAL_TYPES.map((m) => (
                    <Option key={m.value} value={m.value}>{m.label}</Option>
                  ))}
                </Select>
                <FormHelperText>Leave empty to include this item for every roofing material.</FormHelperText>
              </FormControl>

              <Grid container spacing={2}>
                <Grid xs={6} sm={4}>
                  <FormControl>
                    <FormLabel>Sort Order</FormLabel>
                    <Input
                      type="number"
                      slotProps={{ input: { min: 0, step: 1 } }}
                      value={editing.form.sort_order}
                      onChange={(e) => setEditing({ ...editing, form: { ...editing.form, sort_order: e.target.value } })}
                    />
                  </FormControl>
                </Grid>
                <Grid xs={12} sm={8}>
                  <Stack spacing={1} sx={{ pt: { sm: 3.5 } }}>
                    <Checkbox
                      label="Include the measurement's waste %"
                      checked={editing.form.include_waste}
                      onChange={(e) => setEditing({ ...editing, form: { ...editing.form, include_waste: e.target.checked } })}
                    />
                    <Checkbox
                      label="Show on takeoffs"
                      checked={editing.form.is_active}
                      onChange={(e) => setEditing({ ...editing, form: { ...editing.form, is_active: e.target.checked } })}
                    />
                  </Stack>
                </Grid>
              </Grid>

              {formError && <Alert color="danger">{formError}</Alert>}

              <Stack direction="row" spacing={1} sx={{ justifyContent: 'flex-end' }}>
                <Button variant="plain" color="neutral" onClick={() => setEditing(null)}>
                  Cancel
                </Button>
                <Button
                  variant="solid"
                  color="primary"
                  loading={createMutation.isPending || updateMutation.isPending}
                  onClick={handleSave}
                >
                  {editing.item ? 'Save Changes' : 'Add Item'}
                </Button>
              </Stack>
            </Stack>
          )}
        </ModalDialog>
      </Modal>
    </Stack>
  );
};
